import { useState, type ReactNode } from "react";
import { clsx } from "clsx";

export interface TabItem {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  defaultActiveId?: string;
  onChange?: (id: string) => void;
}

export function Tabs({ items, defaultActiveId, onChange }: TabsProps) {
  const [active, setActive] = useState(defaultActiveId ?? items[0]?.id);
  const handle = (id: string) => {
    if (items.find((item) => item.id === id)?.disabled) return;
    setActive(id);
    onChange?.(id);
  };
  return (
    <div>
      {/* DS-conformant UNDERLINE tabs (PERL DS law: "active tab gets primary text + a brand-orange
          underline"). Matches the in-app AppScreenTabs / NodeFacetHeader pattern and is fully
          token-based (theme-aware). Replaces the prior segmented-pill control, which diverged from the
          DS and used static white-alpha borders. */}
      <div
        role="tablist"
        className="mx-2 flex gap-1 border-b border-[var(--color-border-subtle)]"
      >
        {items.map((item) => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              aria-disabled={item.disabled || undefined}
              disabled={item.disabled}
              aria-controls={`tabpanel-${item.id}`}
              id={`tab-${item.id}`}
              onClick={() => handle(item.id)}
              className={clsx(
                // DS-canonical underline: the accent bar is an ABSOLUTE span (DS Tabs.jsx technique), so it
                // adds no height — tab height = py-2 (16) + line-height (20) = 36px, on the 4px grid. (A
                // border-b would add 2px → 38px, off-grid.)
                "relative flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                isActive
                  ? "font-medium text-[var(--color-text-primary)]"
                  : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]",
              )}
            >
              {item.label}
              <span
                aria-hidden
                className={clsx(
                  "absolute inset-x-0 bottom-0 h-0.5 origin-left bg-[var(--color-accent)] transition-transform",
                  isActive ? "scale-x-100" : "scale-x-0",
                )}
              />
            </button>
          );
        })}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`tabpanel-${item.id}`}
          aria-labelledby={`tab-${item.id}`}
          hidden={active !== item.id}
          className="pt-1"
        >
          {item.content}
        </div>
      ))}
    </div>
  );
}
